import type { CSSProperties, ReactNode } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  Info,
  Inbox,
  XCircle,
} from 'lucide-react';
import css from './Superficie.module.css';
import { cx } from './cx';

/* ── Cartão ─────────────────────────────────────────────────────────────── */

interface PropsCartao {
  children: ReactNode;
  /** Cabeçalho com linha divisória. */
  titulo?: ReactNode;
  descricao?: ReactNode;
  acoes?: ReactNode;
  /** Faixa de ações no rodapé. */
  rodape?: ReactNode;
  /**
   * `0` tira o padding (tabela encostada na borda). Números continuam
   * aceitos por compatibilidade, mas prefira `compacto`.
   */
  padding?: 0 | number | string;
  compacto?: boolean;
  className?: string;
  style?: CSSProperties;
  as?: 'div' | 'section' | 'article';
}

export function Cartao({
  children,
  titulo,
  descricao,
  acoes,
  rodape,
  padding,
  compacto,
  className,
  style,
  as: Tag = 'div',
}: PropsCartao) {
  const semPadding = padding === 0 || padding === '0';
  const corpoClasse = semPadding
    ? css.semPadding
    : compacto
      ? css.corpoCompacto
      : css.corpo;
  // Padding numérico diferente do padrão: legado, aplicado direto.
  const corpoEstilo =
    !semPadding && padding !== undefined ? { padding } : undefined;

  return (
    <Tag className={cx(css.cartao, className)}>
      {(titulo || acoes) && (
        <header className={css.cabecalhoCartao}>
          <div>
            {titulo && <h3 className={css.tituloCartao}>{titulo}</h3>}
            {descricao && <p className={css.descricaoCartao}>{descricao}</p>}
          </div>
          {acoes && <div className={css.acoesCartao}>{acoes}</div>}
        </header>
      )}
      <div className={corpoClasse} style={{ ...corpoEstilo, ...style }}>
        {children}
      </div>
      {rodape && <footer className={css.rodapeCartao}>{rodape}</footer>}
    </Tag>
  );
}

export function TituloSecao({
  children,
  descricao,
  acao,
}: {
  children: ReactNode;
  descricao?: ReactNode;
  acao?: ReactNode;
}) {
  return (
    <div className={css.secao}>
      <div>
        <h2 className={css.secaoTitulo}>{children}</h2>
        {descricao && <p className={css.secaoDescricao}>{descricao}</p>}
      </div>
      {acao}
    </div>
  );
}

/* ── Etiqueta ───────────────────────────────────────────────────────────── */

export type TomEtiqueta = 'ok' | 'alerta' | 'erro' | 'neutro' | 'info';

export function Etiqueta({
  tom = 'neutro',
  ponto = true,
  pulsando,
  title,
  children,
}: {
  tom?: TomEtiqueta;
  /** Bolinha de status à esquerda. */
  ponto?: boolean;
  /** Para estados "em andamento". */
  pulsando?: boolean;
  title?: string;
  children: ReactNode;
}) {
  return (
    <span
      className={cx(css.etiqueta, css[tom], pulsando && css.pulsando)}
      title={title}
    >
      {ponto && <span className={css.ponto} aria-hidden="true" />}
      {children}
    </span>
  );
}

/* ── Aviso ──────────────────────────────────────────────────────────────── */

const ICONES = {
  ok: CheckCircle2,
  alerta: AlertTriangle,
  erro: XCircle,
  info: Info,
  neutro: Info,
} as const;

export function Aviso({
  tom = 'erro',
  titulo,
  acao,
  children,
}: {
  tom?: TomEtiqueta;
  titulo?: ReactNode;
  /** Botão à direita ("Recarregar"). */
  acao?: ReactNode;
  children?: ReactNode;
}) {
  const Icone = ICONES[tom];
  return (
    <div
      // Erro precisa ser anunciado na hora; o resto é informativo.
      role={tom === 'erro' ? 'alert' : 'status'}
      className={cx(css.aviso, css[tom])}
    >
      <Icone className={css.avisoIcone} aria-hidden="true" />
      <div className={css.avisoConteudo}>
        {titulo && <strong className={css.avisoTitulo}>{titulo}</strong>}
        {children}
      </div>
      {acao && <div className={css.avisoAcao}>{acao}</div>}
    </div>
  );
}

/* ── Vazio ──────────────────────────────────────────────────────────────── */

export function Vazio({
  titulo,
  descricao,
  acao,
  icone,
}: {
  titulo: string;
  descricao: ReactNode;
  acao?: ReactNode;
  icone?: ReactNode;
}) {
  return (
    <div className={css.vazio}>
      <span className={css.vazioIcone} aria-hidden="true">
        {icone ?? <Inbox />}
      </span>
      <p className={css.vazioTitulo}>{titulo}</p>
      <p className={css.vazioDescricao}>{descricao}</p>
      {acao && <div className={css.vazioAcao}>{acao}</div>}
    </div>
  );
}

/* ── Esqueleto e progresso ──────────────────────────────────────────────── */

export function Esqueleto({
  altura = 16,
  largura = '100%',
  raio,
}: {
  altura?: number | string;
  largura?: number | string;
  raio?: number;
}) {
  return (
    <span
      aria-hidden="true"
      className={css.esqueleto}
      style={{ height: altura, width: largura, borderRadius: raio }}
    />
  );
}

export function Progresso({
  valor,
  rotulo,
  tom,
}: {
  /** 0 a 1. */
  valor: number;
  rotulo?: string;
  tom?: 'alerta' | 'erro';
}) {
  const pct = Math.min(100, Math.max(0, valor * 100));
  return (
    <div
      role="progressbar"
      aria-valuenow={Math.round(pct)}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={rotulo}
      className={cx(
        css.progresso,
        tom === 'alerta' && css.progressoAlerta,
        tom === 'erro' && css.progressoErro,
      )}
    >
      <div className={css.progressoBarra} style={{ width: `${pct}%` }} />
    </div>
  );
}
